import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Supplier, SupplierWrite, InputMaxLength } from "types"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import useSuppliers from "@/hooks/useSuppliers"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import TextInput from "components/inputs/textInput"
import ProcessingButton from "components/inputs/processingButton"
import EmailInput from "@/components/inputs/emailInput"
import TextareaInput from "@/components/inputs/textareaInput"
import Button from "@/components/inputs/button"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import useLayout from "@/hooks/useLayout"
import Section from "@/components/layout/section"

interface EditSupplierFormProps {
    supplier: Supplier
    storageId: string
    supplierId: string
}

const EditSupplierForm = ({ supplier, storageId, supplierId }: EditSupplierFormProps) => {

    const { t } = useTranslation()

    const {
        addToast
    } = useLayout()

    const {
        updateSupplierAsync,
        isUpdating
    } = useSuppliers({ storageId, supplierId })

    // State.
    const [name, setName] = useState(supplier?.name || ``)
    const [email, setEmail] = useState(supplier?.contactInformation?.email || ``)
    const [phone, setPhone] = useState(supplier?.contactInformation?.phone || ``)
    const [notes, setNotes] = useState(supplier?.notes || ``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.info('Edit supplier form submitted successfully')
        event.preventDefault()
        setErrors([])
        
        try {

            const supplierData = {
                ids: {
                    storageId,
                    supplierId
                },
                supplierId: supplier?.supplierId,
                name,
                notes,
                contactInformation: {
                    email,
                    phone
                }
            } as SupplierWrite

            await updateSupplierAsync(supplierData)
            logger.info('Supplier updated successfully', { storageId, supplierId })
            addToast({ text: t('toastSupplierUpdated'), colorScheme: 'green' })
        } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e instanceof Error ? e.message : String(e))
            setErrors([errorMessage])
            addToast({ text: errorMessage, colorScheme: 'red' })
            logger.error('Failed to update supplier', { error: errorMessage, storageId, supplierId })
        }
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle phone change.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value)
    }

    // Handle notes change.
    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Return the JSX.
    return (
        <Form onSubmit={handleSubmit}>

            <Section>

                <TextInput
                    label={t('labelSupplierId')}
                    value={supplier?.supplierId?.toString() || ''}
                    onChange={() => {}}
                    required
                    disabled
                    maxLength={InputMaxLength.short}
                />

                <TextInput
                    label={t('labelSupplierName')}
                    value={name}
                    onChange={handleNameChange}
                    required
                    maxLength={InputMaxLength.short}
                />

                <EmailInput
                    label={t('labelSupplierEmail')}
                    value={email}
                    onChange={handleEmailChange}
                    maxLength={InputMaxLength.medium}
                />

                <TextInput
                    label={t('labelSupplierPhone')}
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={InputMaxLength.short}
                />

                <TextareaInput
                    label={t('labelSupplierNotes')}
                    value={notes}
                    onChange={handleNotesChange}
                    maxLength={InputMaxLength.extraLong}
                />

            </Section>

            <Section>
                <div className={`input-holder`}>
                    {isUpdating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary"><AddIcon /><span>{t('buttonSaveChanges')}</span></Button>
                    )}
                </div>
            </Section>
            
            <FormErrors errors={errors} />

        </Form>
    )
}

export default EditSupplierForm
